import { z } from 'zod'

import type { ExerciseDTO } from '../api/models'
import { ExerciseApi } from '../api'
import { toDashboardAccessBlockedError } from '../utils/dashboard-access-errors'
import { getApiConfig } from './api-config'
import { nullableStringSchema, yearWeekSchema } from './dal-utils'
import { normalizeDateForApiQuery } from './exercise-date-utils'

export interface ProgressCompletionContext {
  contentfulProgramId?: string | null
  contentfulSubjectId?: string | null
  contentfulWeekId?: string | null
}

export const progressRecordSchema = z.object({
  contentfulProgramId: nullableStringSchema,
  contentfulSubjectId: nullableStringSchema,
  contentfulWeekId: nullableStringSchema,
  contentfulSessionId: nullableStringSchema,
  timeSpent: z.number(),
})

export type ProgressRecord = z.infer<typeof progressRecordSchema>

const progressSnapshotSchema = z.object({
  progressItems: z.array(progressRecordSchema),
  totalTimeSpent: z.number(),
})

const toRequiredContentfulId = (value: string | null | undefined) => {
  return value ?? ''
}

const sumProgressTimeSpent = (progressItems: ProgressRecord[]) => {
  return progressItems.reduce((sum, progressItem) => {
    const timeSpent = progressItem.timeSpent
    if (timeSpent < 0) {
      return sum
    }

    return sum + timeSpent
  }, 0)
}

export const ProgressAPI = {
  saveProgress: async (
    sessionId: string,
    timeSpent: number,
    context?: ProgressCompletionContext,
  ) => {
    const exerciseApi = new ExerciseApi(getApiConfig())
    const exercisePayload: ExerciseDTO = {
      date: normalizeDateForApiQuery(new Date()),
      timeSpent,
      contentfulProgramId: toRequiredContentfulId(context?.contentfulProgramId),
      contentfulSubjectId: toRequiredContentfulId(context?.contentfulSubjectId),
      contentfulWeekId: toRequiredContentfulId(context?.contentfulWeekId),
      contentfulSessionId: toRequiredContentfulId(sessionId),
    }
    await exerciseApi.apiExercisePost({ exerciseDTO: exercisePayload })
  },

  getProgressSnapshot: async (yearWeek: string) => {
    const parsedYearWeek = yearWeekSchema.parse(yearWeek)
    try {
      const exerciseApi = new ExerciseApi(getApiConfig())
      const exercises = await exerciseApi.apiExerciseWeekGet({
        yearWeek: parsedYearWeek,
      })
      const progressItems = z.array(progressRecordSchema).parse(
        exercises.map((exerciseItem) => ({
          contentfulProgramId: exerciseItem.contentfulProgramId ?? null,
          contentfulSubjectId: exerciseItem.contentfulSubjectId ?? null,
          contentfulWeekId: exerciseItem.contentfulWeekId ?? null,
          contentfulSessionId: exerciseItem.contentfulSessionId ?? null,
          timeSpent: exerciseItem.timeSpent,
        })),
      )

      return progressSnapshotSchema.parse({
        progressItems,
        totalTimeSpent: sumProgressTimeSpent(progressItems),
      })
    } catch (error) {
      const blockedError = await toDashboardAccessBlockedError(error)
      if (blockedError != null) {
        throw blockedError
      }

      return progressSnapshotSchema.parse({
        progressItems: [],
        totalTimeSpent: 0,
      })
    }
  },
  saveActiveWeek: async (_week: number) => {
    // The current Swagger contract does not expose a remote active-week endpoint.
    // We keep local-week persistence and report remote sync as unavailable.
    return false
  },
}
